import { useId, useState, type ReactNode } from 'react';
import {
  CalendarMonthOutlined,
  ChevronRightOutlined,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Divider,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import {
  EmptyState,
  DialogSurface,
  FormTextField,
  InlineNotice,
  PillTabs,
  LoadingIndicator,
  GuidePage,
  PageSections,
  ResourceState,
  SectionCard,
  StatusBadge,
  createFocusRingStyles,
  designTokens,
} from '../../design-system';
import { SelfLeaveBalanceCard } from './SelfLeaveBalanceCard';
import { AbsenceCalendar } from './AbsenceCalendar';
import type { AbsenceDateRange } from './useCalendarRangeSelection';
import type { AbsencePort } from './port';
import { buildAbsenceCalendarItems, calendarItemsOnDate, formatAbsenceDate, type AbsenceCalendarItem as TimelineItem } from './absenceCalendarModel';
import { absenceHistoryRangeError, filterAbsenceHistory, sortAbsenceHistory, type AbsenceHistoryStatus } from './absenceHistoryModel';
import type {
  AbsenceResource as ApiResource,
  AbsenceRequestableTimeOffType,
  AbsenceTimeOffBalance as TimeOffBalance,
  AbsenceTimeOffRequest as TimeOffRequest,
  AbsenceTimeOffRow as TimeOffRow,
  AbsenceTimeOffType as TimeOffType,
} from './model';

export type SelfAbsenceOverviewProps = {
  port: AbsencePort;
  fixtureMode: boolean;
  balances: ApiResource<TimeOffBalance[]>;
  timeOffTypes: ApiResource<TimeOffType[]>;
  requestableTypes: AbsenceRequestableTimeOffType[];
  requestableResource?: ApiResource<AbsenceRequestableTimeOffType[]>;
  requests: ApiResource<TimeOffRequest[]>;
  entries: ApiResource<TimeOffRow[]>;
  canRequest: boolean;
  hasWorker: boolean;
  employeeCalendar?: ReactNode;
  onOpenSyncSchedule: () => void;
  showClosed: boolean;
  submitError?: string;
  onShowClosedChange: (value: boolean) => void;
  onDismissSubmitError: () => void;
  onStartRequest: (timeOffTypeId?: string, range?: AbsenceDateRange) => void;
  draft?: { status: string; description: string; onResume: () => void };
  onOpenRequest: (request: TimeOffRequest) => void;
  onOpenEntry: (entry: TimeOffRow) => void;
};

function quantity(value: string | number | undefined): string {
  const parsed = Number(value ?? 0);
  if (!Number.isFinite(parsed)) return String(value ?? '-');
  return new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 2 }).format(parsed);
}

function unitLabel(unit: string | undefined): string {
  const normalized = unit?.toUpperCase() ?? '';
  if (normalized.includes('HOUR')) return '시간';
  if (normalized.includes('MINUTE')) return '분';
  return '일';
}

function quantityWithUnit(value: string | number | undefined, unit: string | undefined): string {
  return `${quantity(value)}${unitLabel(unit)}`;
}

function dateLabel(value: string | undefined): string {
  return value ? value.replaceAll('-', '.') : '-';
}

function periodLabel(startDate: string, endDate: string): string {
  return startDate === endDate ? dateLabel(startDate) : `${dateLabel(startDate)} – ${dateLabel(endDate)}`;
}

function HistoryLoading() {
  return <Stack sx={{ minHeight: 150, alignItems: 'center', justifyContent: 'center' }}><LoadingIndicator label="휴가 기록을 불러오는 중입니다." /></Stack>;
}

function TimelineRow({ item, compact = false, onOpenRequest, onOpenEntry }: { item: TimelineItem; compact?: boolean; onOpenRequest: (request: TimeOffRequest) => void; onOpenEntry: (entry: TimeOffRow) => void }) {
  const onOpen = item.entry ? () => onOpenEntry(item.entry!) : item.request ? () => onOpenRequest(item.request!) : undefined;
  const body = compact ? <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0, px: 1.5, py: 1.5, textAlign: 'left' }}>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'start', justifyContent: 'space-between' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 0, overflowWrap: 'anywhere' }}>{item.title}</Typography>
      <StatusBadge label={item.statusLabel} tone={item.tone} />
    </Stack>
    <Typography variant="caption" color="text.secondary">{periodLabel(item.startDate, item.endDate)}</Typography>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
      <Typography variant="caption" color="text.secondary">{quantityWithUnit(item.quantity, item.unitOfTime)}</Typography>
      {onOpen && <ChevronRightOutlined sx={{ color: designTokens.color.mutedText, fontSize: 18 }} />}
    </Stack>
  </Stack> : <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 1.5 }} sx={{ width: '100%', px: `${designTokens.layout.sectionCardBodyPadding - designTokens.space.xs}px`, py: 1.4, alignItems: { sm: 'center' }, textAlign: 'left' }}>
    <Stack direction="row" spacing={1.25} sx={{ flex: 1, minWidth: 0, alignItems: 'center' }}>
      <Box sx={{ width: 32, height: 32, flex: '0 0 auto', display: 'grid', placeItems: 'center', borderRadius: designTokens.radius.surface, bgcolor: item.kind === 'request' ? designTokens.color.actionSoft : designTokens.color.overviewSurface, color: item.kind === 'request' ? designTokens.color.action : designTokens.color.secondaryText }}><CalendarMonthOutlined sx={{ fontSize: 18 }} /></Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 740 }}>{item.title}</Typography>
        <Typography variant="caption" color="text.secondary">{periodLabel(item.startDate, item.endDate)} · {quantityWithUnit(item.quantity, item.unitOfTime)}</Typography>
      </Box>
    </Stack>
    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
      <StatusBadge label={item.statusLabel} tone={item.tone} />
      {onOpen && <ChevronRightOutlined sx={{ color: designTokens.color.mutedText, fontSize: 19 }} />}
    </Stack>
  </Stack>;

  if (onOpen) return <ButtonBase
    onClick={onOpen}
    disableRipple
    sx={{
      width: '100%', justifyContent: 'stretch', borderRadius: designTokens.radius.navigation,
      '&:hover': { bgcolor: designTokens.color.hoverSurface },
      '&:focus-visible, &.Mui-focusVisible': createFocusRingStyles({ inset: true }),
    }}
  >{body}</ButtonBase>;
  return body;
}

export function SelfAbsenceOverview({
  port,
  fixtureMode,
  balances,
  timeOffTypes,
  requestableTypes,
  requestableResource,
  requests,
  entries,
  canRequest,
  hasWorker,
  employeeCalendar,
  onOpenSyncSchedule,
  showClosed,
  submitError,
  onShowClosedChange,
  onDismissSubmitError,
  onStartRequest,
  draft,
  onOpenRequest,
  onOpenEntry,
}: SelfAbsenceOverviewProps) {
  const balanceItems = balances.data ?? [];
  const typeItems = timeOffTypes.data ?? [];
  const limitTypes = requestableTypes.filter((type) => type.managementMode === 'USAGE_LIMIT');

  const timeline = buildAbsenceCalendarItems(requests.data ?? [], entries.data ?? [], typeItems);
  const visibleTimeline = timeline.filter((item) => showClosed || !item.closed);
  const recent = sortAbsenceHistory(visibleTimeline).slice(0, 3);
  const [view, setView] = useState<'calendar' | 'employees'>('calendar');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFrom, setHistoryFrom] = useState('');
  const [historyTo, setHistoryTo] = useState('');
  const [historyStatus, setHistoryStatus] = useState<AbsenceHistoryStatus>('');
  const [employeeCalendarVisited, setEmployeeCalendarVisited] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>();
  const [selectedTypeId, setSelectedTypeId] = useState('');
  const [publicHolidays, setPublicHolidays] = useState<ReadonlyMap<string, string>>(() => new Map());
  const selectedPublicHoliday = selectedDate ? publicHolidays.get(selectedDate) : undefined;
  const selectedItems = selectedDate ? calendarItemsOnDate(visibleTimeline, selectedDate).filter((item) => !selectedTypeId || item.typeId === selectedTypeId) : [];
  const tabId = useId();
  const rowActions = { onOpenRequest, onOpenEntry };
  const historyLoading = requests.loading || entries.loading;
  const historyError = requests.error || entries.error;
  const rangeError = absenceHistoryRangeError(historyFrom, historyTo);
  const filteredHistory = filterAbsenceHistory(timeline, { from: historyFrom, to: historyTo, status: historyStatus });
  const resetHistoryFilters = () => { setHistoryFrom(''); setHistoryTo(''); setHistoryStatus(''); };
  const optionLoading = balances.loading || timeOffTypes.loading || Boolean(requestableResource?.loading);
  const optionError = balances.error || timeOffTypes.error || requestableResource?.error;
  const canStartRequest = canRequest && hasWorker && !optionLoading && !optionError && requestableTypes.length > 0;
  const startRangeRequest = (range: AbsenceDateRange, typeId: string) => {
    if (!canStartRequest) return;
    const type = requestableTypes.find((item) => item.id === typeId) ?? requestableTypes[0];
    setSelectedDate(undefined);
    onStartRequest(type.id, range);
  };
  return <><GuidePage
    testId="absence-self-content"
    title="휴가"
    width="fluid"
    primaryAction={canRequest ? <Button variant="fill" disabled={!canStartRequest && !draft} onClick={() => draft ? draft.onResume() : onStartRequest()}>신청하기</Button> : undefined}
    primaryActionVisibility="always"
    actions={hasWorker ? <Button variant="outlined" onClick={onOpenSyncSchedule}>휴가 표시 시간</Button> : undefined}
    tabs={<PillTabs ariaLabel="휴가 보기" value={view} onChange={(next) => { setView(next); if (next === 'employees') setEmployeeCalendarVisited(true); }} items={[
      { value: 'calendar', label: '내 휴가', tabId: `${tabId}-calendar`, panelId: `${tabId}-calendar-panel` },
      ...(employeeCalendar ? [{ value: 'employees' as const, label: '구성원 휴가', tabId: `${tabId}-employees`, panelId: `${tabId}-employees-panel` }] : []),
    ]} />}
  >
    <PageSections>
      {submitError && <Alert severity="error" onClose={onDismissSubmitError}>{submitError}</Alert>}
      {canRequest && draft && <Stack component="section" aria-label="작성 중인 휴가 신청" direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'stretch', sm: 'center' }, justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{draft.status}</Typography>
          <Typography variant="body2" color="text.secondary">{draft.description}</Typography>
        </Box>
        <Button variant="outlined" onClick={draft.onResume}>이어서 작성</Button>
      </Stack>}
      {employeeCalendar && <Box role="tabpanel" id={`${tabId}-employees-panel`} aria-labelledby={`${tabId}-employees`} hidden={view !== 'employees'}>
        {employeeCalendarVisited && employeeCalendar}
      </Box>}
      <Box role="tabpanel" id={`${tabId}-calendar-panel`} aria-labelledby={`${tabId}-calendar`} hidden={view !== 'calendar'}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '240px minmax(0, 1fr)' }, gap: `${designTokens.layout.pageSectionGap}px`, alignItems: 'start' }}>
          <Stack spacing={2} sx={{ minWidth: 0 }}>
            <Box component="section" aria-label="휴가 잔여 현황" data-testid="absence-balance-summary">
              <ResourceState loading={optionLoading} error={optionError?.message} empty={!balanceItems.length && !limitTypes.length}
                onRetry={() => { balances.reload(); timeOffTypes.reload(); requestableResource?.reload(); }}
                loadingLabel="사용 가능한 휴가를 불러오는 중입니다."
                emptyTitle="등록할 수 있는 휴가가 없습니다."
                emptyDescription="적용된 휴가 정책 또는 휴가 유형을 관리자에게 확인해 주세요."
              >
                <Box data-testid="absence-leave-options">
                  <SelfLeaveBalanceCard balances={balanceItems} types={typeItems} limitTypes={limitTypes} canRequest={canStartRequest} onRequest={onStartRequest} />
                </Box>
              </ResourceState>
            </Box>
            <Box component="section" aria-label="신청 이력" data-testid="absence-history-summary">
              <SectionCard title="신청 이력">
                <ResourceState embedded density="compact" loading={historyLoading} error={historyError?.message} empty={!visibleTimeline.length}
                  onRetry={() => { requests.reload(); entries.reload(); }} loadingLabel="신청 이력을 불러오는 중입니다."
                  emptyAction={false}
                  emptyTitle="신청 이력이 없습니다.">
                  <Stack data-testid="absence-recent-list" divider={<Divider />} sx={{ px: 0.5 }}>
                    {recent.map((item) => <TimelineRow key={`${item.kind}-${item.id}`} item={item} compact {...rowActions} />)}
                  </Stack>
                </ResourceState>
                <Box sx={{ p: `${designTokens.layout.surfacePaddingCompact}px` }}>
                  <Button fullWidth variant="outlined" aria-haspopup="dialog" onClick={() => setHistoryOpen(true)}>전체 이력 보기</Button>
                </Box>
              </SectionCard>
            </Box>
          </Stack>
          <Box data-testid="absence-personal-calendar" sx={{ minWidth: 0 }}>
            {historyLoading ? <HistoryLoading /> : historyError ? <ResourceState loading={false} empty={false} error={historyError.message} onRetry={() => { requests.reload(); entries.reload(); }}>{null}</ResourceState> : <AbsenceCalendar port={port} fixtureMode={fixtureMode} items={timeline} showClosed={showClosed} onShowClosedChange={onShowClosedChange} onPublicHolidaysChange={setPublicHolidays} onSelectDate={(date, typeId) => { setSelectedDate(date); setSelectedTypeId(typeId); }} onRequestRange={canStartRequest ? startRangeRequest : undefined} />}
          </Box>
        </Box>
      </Box>
    </PageSections>
  </GuidePage>
    <DialogSurface open={historyOpen} onClose={() => setHistoryOpen(false)} title="전체 신청 이력" closeLabel="전체 신청 이력 닫기" size="large" mobilePresentation="fullscreen">
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <FormTextField fullWidth type="date" label="조회 시작일" value={historyFrom} onChange={(event) => setHistoryFrom(event.target.value)} />
          <FormTextField fullWidth type="date" label="조회 종료일" value={historyTo} onChange={(event) => setHistoryTo(event.target.value)} error={Boolean(rangeError)} helperText={rangeError} />
          <FormTextField fullWidth select label="상태" value={historyStatus} slotProps={{ select: { displayEmpty: true } }} onChange={(event) => setHistoryStatus(event.target.value as AbsenceHistoryStatus)}>
            <MenuItem value="">전체</MenuItem>
            <MenuItem value="PENDING">승인 대기</MenuItem>
            <MenuItem value="CONFIRMED">확정·승인 완료</MenuItem>
            <MenuItem value="DRAFT">작성 중</MenuItem>
            <MenuItem value="REJECTED">반려</MenuItem>
            <MenuItem value="CANCELLED">취소됨</MenuItem>
          </FormTextField>
        </Stack>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography role="status" variant="body2" color="text.secondary">{!historyLoading && !historyError && !rangeError ? `${filteredHistory.length}건` : ''}</Typography>
          <Button onClick={resetHistoryFilters} disabled={!historyFrom && !historyTo && !historyStatus}>필터 초기화</Button>
        </Stack>
        {submitError && <Alert severity="error" onClose={onDismissSubmitError}>{submitError}</Alert>}
        {rangeError ? <InlineNotice severity="info">조회 기간을 확인해 주세요.</InlineNotice> : <ResourceState embedded density="compact" loading={historyLoading} error={historyError?.message} empty={!filteredHistory.length}
          onRetry={() => { requests.reload(); entries.reload(); }} loadingLabel="신청 이력을 불러오는 중입니다."
          emptyAction={false}
          emptyTitle={timeline.length ? '조건에 맞는 신청 이력이 없습니다.' : '신청 이력이 없습니다.'}
          emptyDescription={timeline.length ? '조회 기간이나 상태를 변경해 주세요.' : undefined}>
          <Stack data-testid="absence-history-list" divider={<Divider />}>
            {filteredHistory.map((item) => <TimelineRow key={`${item.kind}-${item.id}`} item={item} {...rowActions}
              onOpenRequest={(request) => { onOpenRequest(request); }}
              onOpenEntry={(entry) => { onOpenEntry(entry); }}
            />)}
          </Stack>
        </ResourceState>}
      </Stack>
    </DialogSurface>
    <DialogSurface open={Boolean(selectedDate)} title="선택한 일정" closeLabel="상세 닫기" size="compact" onClose={() => setSelectedDate(undefined)} actions={canStartRequest && selectedDate ? <Button fullWidth variant="contained" onClick={() => startRangeRequest({ startDate: selectedDate, endDate: selectedDate }, selectedTypeId)}>이 날짜로 신청</Button> : undefined}>
        <Box component="section" aria-label="선택한 일정">
          <Typography component="h2" variant="h6" sx={{ mb: 2 }}>{selectedDate ? formatAbsenceDate(selectedDate) : ''}</Typography>
          {selectedPublicHoliday && <Typography role="note" variant="body2" sx={{ mb: 2, color: designTokens.color.dangerText }}>공휴일 · {selectedPublicHoliday}</Typography>}
          {selectedItems.length ? <Stack divider={<Divider />}>
            {selectedItems.map((item) => <TimelineRow key={`${item.kind}-${item.id}`} item={item} {...rowActions}
              onOpenRequest={(request) => { onOpenRequest(request); }}
              onOpenEntry={(entry) => { onOpenEntry(entry); }}
            />)}
          </Stack> : <EmptyState>이 날짜에 휴가 일정이 없습니다.</EmptyState>}
        </Box>
    </DialogSurface>
  </>;
}
